import { useMutation, useQuery } from '@apollo/client';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Visibility,
  VisibilityOff,
  Warning as WarningIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  LockOutlined as LockOutlinedIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
} from '@mui/material';
import { TurnstileWidget } from '../components/TurnstileWidget';

import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/common/Logo';
import { useValidation } from '../hooks/useValidation';
import { ACCEPT_MEMBER_INVITE_MUTATION, PREVIEW_WORKSPACE_INVITE_QUERY } from '../services/graphql';

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  let score = 0;

  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 2) return { score: (score / 6) * 100, label: 'Weak', color: '#d32f2f' };
  if (score <= 4) return { score: (score / 6) * 100, label: 'Fair', color: '#ed6c02' };
  return { score: (score / 6) * 100, label: 'Strong', color: '#2e7d32' };
}

const STEPS = ['Verify Email', 'Create Password'];

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const workspaceInviteToken = searchParams.get('inviteToken');
  const { requestSignupEmailCode, signup } = useAuth();
  const { validateEmail, validatePassword } = useValidation();

  // Extract token from redirect URL if it's a member invite (legacy flow)
  const memberInviteToken = redirect?.match(/\/member-invite\/([^?]+)/)?.[1];
  
  const [acceptMemberInvite] = useMutation(ACCEPT_MEMBER_INVITE_MUTATION);

  // Fetch workspace invite preview if token is present
  const { data: invitePreviewData, loading: invitePreviewLoading } = useQuery(
    PREVIEW_WORKSPACE_INVITE_QUERY,
    {
      variables: { token: workspaceInviteToken },
      skip: !workspaceInviteToken,
      fetchPolicy: 'network-only',
    }
  );

  const invitePreview = invitePreviewData?.previewWorkspaceInvite;
  const isInviteMode = !!workspaceInviteToken && !!invitePreview;
  const inviteInvalid = workspaceInviteToken && !invitePreviewLoading && !invitePreview;
  const passwordStepIndex = isInviteMode ? 0 : 1;

  // Step management
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (isInviteMode) {
      setActiveStep(0);
    }
  }, [isInviteMode]);

  // Step 1: Email verification
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Set email from invite preview when available
  useEffect(() => {
    if (invitePreview?.invitedEmail) {
      setEmail(invitePreview.invitedEmail);
    }
  }, [invitePreview?.invitedEmail]);

  // Step 2: Password creation
  const [masterPassword, setMasterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0); // For resetting Turnstile

  const passwordStrength = getPasswordStrength(masterPassword);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleTurnstileError = useCallback((errorMsg: string) => {
    setError(errorMsg);
    setTurnstileToken(null);
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken(null);
    setTurnstileKey((k: number) => k + 1);
  };

  // Step 1: Request verification code
  const handleSendCode = async () => {
    setError('');
    setSuccess('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);

    try {
      const result = await requestSignupEmailCode(email, turnstileToken);
      resetTurnstile();

      if (result.success) {
        setCodeSent(true);
        setSuccess('Verification code sent! Check your email inbox.');
        setResendCooldown(60);
      } else {
        if (result.retryAfter) {
          setResendCooldown(result.retryAfter);
          setError(`Please wait ${result.retryAfter} seconds before requesting another code`);
        } else {
          setError(result.message || 'Failed to send verification code');
        }
      }
    } catch (err) {
      resetTurnstile();
      const message = err instanceof Error ? err.message : 'Failed to send verification code';
      // Handle specific error codes
      if (message.includes('EMAIL_DISPOSABLE')) {
        setError('Disposable email addresses are not allowed');
      } else if (message.includes('EMAIL_QUOTA')) {
        setError('We are unable to send emails at this time. Please try again later.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle code input change with formatting
  const handleCodeChange = (value: string) => {
    // Only allow digits, max 6
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setEmailCode(cleaned);
  };

  // Step 1: Proceed to password step
  const handleContinueToPassword = () => {
    setError('');
    setSuccess('');

    if (emailCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setActiveStep(1);
  };

  // Step 2: Final signup
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const passwordValidation = validatePassword(masterPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.message);
      return;
    }

    if (masterPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);

    try {
      // Pass workspace invite token if present - backend will auto-join the workspace
      const result = await signup(
        email,
        masterPassword,
        isInviteMode ? undefined : emailCode,
        turnstileToken,
        workspaceInviteToken || undefined
      );

      // If this is a legacy member invite signup, accept the invite after signup
      if (memberInviteToken) {
        try {
          await acceptMemberInvite({
            variables: {
              input: { token: memberInviteToken },
            },
          });
        } catch (acceptErr) {
          setError(acceptErr instanceof Error ? acceptErr.message : 'Failed to auto-accept member invite. You can accept it manually later.');
          // Continue anyway - user can accept manually
        }
      }

      // Redirect based on whether user needs to create a workspace
      if (result.needsWorkspaceSetup) {
        navigate('/onboarding');
      } else {
        // User joined a workspace via invite, go to vault
        navigate('/vault');
      }
    } catch (err) {
      resetTurnstile();
      const message = err instanceof Error ? err.message : 'Signup failed';
      // Handle specific error codes
      if (message.includes('EMAIL_CODE_INVALID')) {
        setError('Invalid verification code. Please check and try again.');
      } else if (message.includes('EMAIL_CODE_EXPIRED')) {
        setError('Verification code has expired. Please request a new one.');
        if (!isInviteMode) {
          setActiveStep(0);
          setEmailCode('');
          setCodeSent(false);
        }
      } else if (message.includes('EMAIL_CODE_TOO_MANY_ATTEMPTS')) {
        setError('Too many failed attempts. Please request a new code.');
        if (!isInviteMode) {
          setActiveStep(0);
          setEmailCode('');
          setCodeSent(false);
        }
      } else if (message.includes('USER_EXISTS')) {
        setError('An account with this email already exists');
      } else if (message.includes('INVITE_EMAIL_MISMATCH')) {
        setError('This invitation was sent to a different email address.');
      } else if (message.includes('INVITE_EXPIRED_OR_INVALID')) {
        setError('The invitation has expired or is no longer valid.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Logo size="large" />
            </Box>
            <Typography color="text.secondary">
              Create your secure vault
            </Typography>
          </Box>

          {/* Show loading state while fetching invite preview */}
          {workspaceInviteToken && invitePreviewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Show invalid invite error */}
          {inviteInvalid && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              icon={<BusinessIcon />}
            >
              <Typography variant="body2" fontWeight={500}>
                Invalid or Expired Invitation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                This invitation link is no longer valid. It may have expired or already been used.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to="/signup"
                >
                  Continue with regular signup
                </Button>
              </Box>
            </Alert>
          )}

          {/* Show invite context banner */}
          {isInviteMode && (
            <Alert 
              severity="info" 
              icon={<BusinessIcon />}
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" fontWeight={500}>
                You're invited to join "{invitePreview.workspaceName}"
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                This invitation is for <strong>{invitePreview.invitedEmail}</strong>.
                {' '}Complete signup to join the workspace.
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Only show stepper when not loading and not invalid invite */}
          {(!workspaceInviteToken || (!invitePreviewLoading && !inviteInvalid)) && (
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 1: Email Verification */}
            {!isInviteMode && (
              <Step>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: activeStep >= 0 ? 'primary.main' : 'grey.300',
                        color: 'white',
                      }}
                    >
                      {activeStep > 0 ? <CheckCircleIcon fontSize="small" /> : <EmailIcon fontSize="small" />}
                    </Box>
                  )}
                >
                  <Typography fontWeight={500}>{STEPS[0]}</Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ pt: 1, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      We'll send a 6-digit verification code to your email.
                    </Typography>

                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => !isInviteMode && setEmail(e.target.value)}
                      disabled={isInviteMode || (codeSent && resendCooldown > 0)}
                      margin="normal"
                      autoComplete="email"
                      autoFocus={!isInviteMode}
                    />

                    {!codeSent && (
                      <>
                        <TurnstileWidget
                          key={turnstileKey}
                          onSuccess={handleTurnstileSuccess}
                          onExpire={handleTurnstileExpire}
                          onError={handleTurnstileError}
                          helperText={!turnstileToken ? 'Complete verification to continue' : undefined}
                        />

                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleSendCode}
                          disabled={loading || !turnstileToken}
                          sx={{ mt: 2 }}
                        >
                          {loading ? 'Sending...' : 'Send Verification Code'}
                        </Button>
                      </>
                    )}

                    {codeSent && (
                      <>
                        <TextField
                          fullWidth
                          label="Verification Code"
                          value={emailCode}
                          onChange={(e) => handleCodeChange(e.target.value)}
                          margin="normal"
                          placeholder="000000"
                          inputProps={{
                            maxLength: 6,
                            style: {
                              letterSpacing: '0.5em',
                              textAlign: 'center',
                              fontSize: '1.5rem',
                              fontFamily: 'monospace',
                            },
                          }}
                          helperText="Enter the 6-digit code from your email"
                        />

                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={handleSendCode}
                            disabled={loading || resendCooldown > 0 || !turnstileToken}
                            sx={{ flex: 1 }}
                          >
                            {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend Code'}
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleContinueToPassword}
                            disabled={emailCode.length !== 6}
                            sx={{ flex: 1 }}
                          >
                            Continue
                          </Button>
                        </Box>

                        {resendCooldown === 0 && !turnstileToken && (
                          <Box sx={{ mt: 2 }}>
                            <TurnstileWidget
                              key={turnstileKey}
                              onSuccess={handleTurnstileSuccess}
                              onExpire={handleTurnstileExpire}
                              onError={handleTurnstileError}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </StepContent>
              </Step>
            )}

            {/* Step 2: Password Creation */}
            <Step>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: activeStep >= passwordStepIndex ? 'primary.main' : 'grey.300',
                      color: 'white',
                    }}
                  >
                    <LockIcon fontSize="small" />
                  </Box>
                )}
              >
                <Typography fontWeight={500}>{STEPS[1]}</Typography>
              </StepLabel>
              <StepContent>
                <form onSubmit={handleSubmit}>
                  {isInviteMode && (
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      margin="normal"
                      autoComplete="email"
                      disabled
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <LockOutlinedIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      helperText="This email is set by the invitation and cannot be changed."
                    />
                  )}
                  <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Your master password encrypts your vault
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      If you forget it, AmnPass cannot recover your data.
                      Store your master password safely.
                    </Typography>
                  </Alert>

                  <TextField
                    fullWidth
                    label="Master Password"
                    type={showPassword ? 'text' : 'password'}
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    margin="normal"
                    autoComplete="new-password"
                    autoFocus
                    helperText="Minimum 12 characters. Use a mix of letters, numbers, and symbols."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {masterPassword && (
                    <Box sx={{ mt: 1, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Password strength
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: passwordStrength.color }}
                        >
                          {passwordStrength.label}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength.score}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: passwordStrength.color,
                          },
                        }}
                      />
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    label="Confirm Master Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    margin="normal"
                    autoComplete="new-password"
                    error={confirmPassword !== '' && confirmPassword !== masterPassword}
                    helperText={
                      confirmPassword !== '' && confirmPassword !== masterPassword
                        ? 'Passwords do not match'
                        : ''
                    }
                  />

                  <TurnstileWidget
                    key={`signup-${turnstileKey}`}
                    onSuccess={handleTurnstileSuccess}
                    onExpire={handleTurnstileExpire}
                    onError={handleTurnstileError}
                    helperText={!turnstileToken ? 'Complete verification to continue' : undefined}
                  />

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {!isInviteMode && (
                      <Button
                        variant="outlined"
                        onClick={() => setActiveStep(0)}
                        sx={{ flex: 1 }}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || !turnstileToken}
                      sx={{ flex: 1 }}
                    >
                      {loading ? 'Creating Vault...' : 'Create Vault'}
                    </Button>
                  </Box>
                </form>
              </StepContent>
            </Step>
          </Stepper>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Already have an account?{' '}
              <Link component={RouterLink} to="/login">
                Sign in
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
